import './TiersPanel.css';

import { useMembershipKpis, useMembershipTypesWithStats } from '../../queries';
import TierKpiStrip from './TierKpiStrip';
import TierCard from './TierCard';
import BenefitsByTierMatrix from './BenefitsByTierMatrix';
import NoTiersEmptyState from '../empty/NoTiersEmptyState';
import NoMembersEmptyState from '../empty/NoMembersEmptyState';

type Props = {
  membershipEnabled: boolean;
  onCreateTier: () => void;
  onIssueMembership: () => void;
  onEditTier?: (typeUuid: string) => void;
  onEditBenefits?: () => void;
};

const TiersPanel = ({
  membershipEnabled,
  onCreateTier,
  onIssueMembership,
  onEditTier,
  onEditBenefits,
}: Props) => {
  const kpisQuery = useMembershipKpis(membershipEnabled);
  const typesQuery = useMembershipTypesWithStats(membershipEnabled);

  const isLoading = kpisQuery.isLoading || typesQuery.isLoading;
  const tiers = typesQuery.data?.items ?? [];
  const kpis = kpisQuery.data ?? null;

  // Two distinct empty states, per §12.3 of the brief.
  if (!isLoading && tiers.length === 0) {
    return <NoTiersEmptyState onCreateTier={onCreateTier} />;
  }

  // Tiers exist but no deliberately enrolled members yet. KPI strip stays
  // present but renders `—` for every value because the snapshot returns
  // `null` everywhere when totalMembers is null.
  const noMembersYet = !isLoading && tiers.length > 0 && (kpis?.totalMembers ?? 0) === 0;

  return (
    <div className="tiers-panel">
      <TierKpiStrip data={kpis} isLoading={isLoading} />
      <div className="tiers-panel__grid">
        {tiers.map((tier, index) => (
          <TierCard
            key={tier.membershipTypeUuid}
            tier={tier}
            index={index}
            onEdit={onEditTier}
          />
        ))}
      </div>
      {noMembersYet ? (
        <NoMembersEmptyState onIssueMembership={onIssueMembership} />
      ) : null}
      <BenefitsByTierMatrix tiers={tiers} onEditBenefits={onEditBenefits} />
    </div>
  );
};

export default TiersPanel;
